import { z } from 'zod';
import { PROVIDER_SCHEDULING_TYPES } from '@provider-directory/constants';
import {
  availabilityResourceSchema,
  entityNameResourceSchema,
  hospitalAssociationsSchema,
  NearBySearchCriteria,
  pagedResponseSchema,
  urlResourceSchema,
  visitTypeResourceSchema,
} from './common.contracts';
import { locationResourceSchema } from './locations.contracts';

export const Realm = z.enum(['adult', 'peds']);
export type Realm = z.infer<typeof Realm>;

export const UnifiedProviderSearchTargets = z.enum([
  'specialties',
  'specialtySynonyms',
]);
export type UnifiedProviderSearchTargets = z.infer<
  typeof UnifiedProviderSearchTargets
>;
export interface ProvidersSearchCriteria extends NearBySearchCriteria {
  terms?: string;
  npi?: string;
  providerName?: string;
  specialties?: string[];
  isPcp?: boolean;
  acceptingNewPatients?: boolean;
  acceptingVirtualVisits?: boolean;
  allowsOpenScheduling?: boolean;
  hasSlots?: boolean;
  timeOfDay?: ('morning' | 'afternoon' | 'evening')[];
  daysOfWeek?: ('mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun')[];
  canBookOnline?: boolean;
  canOnlineSchedule?: boolean;
  includeSemiMatches?: boolean;
  withAvailability?: boolean;
  ageGroups?: Realm | Realm[];
  gender?: string;
  // A single URL query param is a string; a repeated one is a string[].
  // Languages are an open/dynamic set (unlike the fixed Realm age groups), so
  // values are not constrained here and may be invalid until validated against
  // the search facets (see stripInvalidFacetValues).
  languages?: string | string[];
  capabilities?: string[];
  sort?: string;
  facets?: ProvidersFacet[];
  hospitalAssociations?: string;
  unified?: string[];
  categories?: UnifiedProviderSearchTargets[];
  reasonForVisit?: string;
  providerGroup?: string;
  // TODO: Remove these below once we have something like a useSearchCriteria hook that sanitizes params
  ['visit-types']?: string;
  ['reason-for-visit']?: string;
}

/**
 * Request interfaces
 */

export const ProvidersRequestFacetEnum = z.enum([
  'specialties',
  'gender',
  'languages',
  'acceptingVirtualVisits',
  'acceptingNewPatients',
  'canBookOnline',
  'allowsOpenScheduling',
  'isPcp',
  'ageGroups',
  'visitTypes',
  'capabilities',
  'hospitalAssociations',
  'reasonForVisit',
  'providerGroup',
  'timeOfDay',
  'daysOfWeek',
]);
export type ProvidersFacet = z.infer<typeof ProvidersRequestFacetEnum>;

// Response facets map isPcp to isPrimaryCare
export const ProvidersResponseFacetEnum = z.enum([
  ...ProvidersRequestFacetEnum.exclude(['isPcp']).options,
  'isPrimaryCare',
]);

enum ProviderSorting {
  scoreAsc = '+score',
  scoreDesc = '-score',
  availability = 'availability',
  availabilityAsc = '+availability',
  availabilityDesc = '-availability',
  distance = 'distance',
  distanceAsc = '+distance',
  distanceDesc = '-distance',
  relevanceAsc = '+relevance',
  relevanceDesc = '-relevance',
  name = 'name',
  nameDesc = '-name',
  networks = 'networks',
  networksAsc = '+networks',
  networksDesc = '-networks',
}
export const ProviderSortingEnum = z.enum(ProviderSorting);
export type ProviderSortingEnum = z.infer<typeof ProviderSortingEnum>;

/**
 * Response interfaces
 */

const reasonForVisitResourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  hospitalAssociation: z.string().optional(),
  ehrVisitTypeId: z.string(),
});

export type ReasonForVisitResource = z.infer<
  typeof reasonForVisitResourceSchema
>;

export const providerLocationResourceSchema = locationResourceSchema.extend({
  distance: z.number().optional(),
  isPrimaryLocation: z.boolean(),
  availability: z.number().default(0),
  visitTypes: z.array(visitTypeResourceSchema),
  reasonsForVisit: z.array(reasonForVisitResourceSchema),
  outsideSearchRadius: z.boolean().nullable(),
});
export type ProviderLocationResource = z.infer<
  typeof providerLocationResourceSchema
>;

export const providerSpecialtyResourceSchema = z.object({
  name: z.string(),
  isPrimary: z.boolean(),
});
export type ProviderSpecialtyResource = z.infer<
  typeof providerSpecialtyResourceSchema
>;

export const providerVisitTypeResourceSchema = visitTypeResourceSchema.extend({
  availability: availabilityResourceSchema,
});
export type ProviderVisitTypeResource = z.infer<
  typeof providerVisitTypeResourceSchema
>;

const providerSlotsAvailabilityResourceSchema = z.object({
  date: z.string(),
  start: z.string(),
  ehrLocationId: z.string(),
  ehrVisitTypeId: z.string(),
});
export type ProviderSlotsAvailabilityResource = z.infer<
  typeof providerSlotsAvailabilityResourceSchema
>;

const providerDegreeResourceSchema = z.object({
  name: z.string(),
});
export type ProviderDegreeResource = z.infer<
  typeof providerDegreeResourceSchema
>;

export const Gender = z.enum(['male', 'female', 'other']);
export type Gender = z.infer<typeof Gender>;

const providerSchedulingTypeSchema = z.enum(PROVIDER_SCHEDULING_TYPES);

export type ProviderSchedulingType = z.infer<
  typeof providerSchedulingTypeSchema
>;

const EMPLOYED_KEY = 'Employed';
const AFFILIATE_KEY = 'Affiliate';
const COUFP_KEY = 'COUFP';
export const Status = z.enum([EMPLOYED_KEY, AFFILIATE_KEY, COUFP_KEY]);
export type Status = z.infer<typeof Status>;

export const providerDateCachedSlotsResourceSchema = z.object({
  date: z.string(),
  slots: z.number(),
});

export type ProviderDateCachedSlotsResource = z.infer<
  typeof providerDateCachedSlotsResourceSchema
>;

export const locationCachedSlotsResourceSchema = z.object({
  departmentId: z.string(),
  days: z.array(providerDateCachedSlotsResourceSchema),
});

export type LocationCachedSlotsResource = z.infer<
  typeof locationCachedSlotsResourceSchema
>;

export const providerLinkResourceSchema = z.object({
  // http(s) only — rejects javascript:/data:/etc. at parse time
  url: z.httpUrl(),
  displayName: z.string().optional(),
  order: z.number(),
  mediaType: z.string().optional(),
  source: z.string().optional(),
  // ISO date ("YYYY-MM-DD" or "YYYY-MM"); rendered as month + year.
  date: z.string().optional(),
});

export type ProviderLinkResource = z.infer<typeof providerLinkResourceSchema>;

export const providerResourceSchema = z.object({
  npi: z.string(),
  name: entityNameResourceSchema,
  gender: z.nullable(Gender),
  ageGroups: z.array(Realm),
  languages: z.array(z.string()),
  urls: z.array(urlResourceSchema),
  isPrimaryCare: z.boolean(),
  acceptingNewPatients: z.boolean(),
  canBookOnline: z.boolean(),
  acceptingVirtualVisits: z.boolean(),
  allowsOpenScheduling: z.boolean(),
  ehrExternalId: z.string().nullable(),
  degrees: z.array(providerDegreeResourceSchema),
  specialties: z.array(providerSpecialtyResourceSchema),
  locations: z.array(providerLocationResourceSchema),
  visitTypes: z.array(providerVisitTypeResourceSchema),
  availability: availabilityResourceSchema.optional(),
  globalAvailability: availabilityResourceSchema.optional(),
  hospitalAssociations: hospitalAssociationsSchema,
  slotsAvailability: z
    .array(providerSlotsAvailabilityResourceSchema)
    .optional(),
  scheduling: providerSchedulingTypeSchema.nullable().optional(),
  status: z.nullable(Status),
  professionalTitles: z.array(z.string()),
  schedule: z.array(locationCachedSlotsResourceSchema).optional(),
  matchTier: z.enum(['exact', 'soft']).optional(),
});

export type ProviderResource = z.infer<typeof providerResourceSchema>;

export const appointmentProviderSchema = providerResourceSchema
  .partial()
  .required({
    npi: true,
    name: true,
    specialties: true,
    locations: true,
  })
  .nullable();
export type AppointmentProvider = z.infer<typeof appointmentProviderSchema>;

export const providerDetailsResourceSchema = providerResourceSchema.extend({
  biography: z.string().nullable(),
  clinicalFocus: z.string().nullable(),
  researchFocus: z.string().nullable(),
  fellowships: z.array(z.string()),
  medicalEducation: z.array(z.string()),
  residencies: z.array(z.string()),
  internship: z.string().nullable(),
  conditions: z.array(z.string()).nullish(),
  boardCertifications: z.array(z.string()).nullish(),
  organizationTitles: z.array(z.string()).optional(),
  // `.catch` keeps a malformed links payload from failing the whole provider
  links: z.array(providerLinkResourceSchema).optional().catch(undefined),
});

export type ProviderDetailsResource = z.infer<
  typeof providerDetailsResourceSchema
>;

export const facetTermResourceSchema = z.object({
  value: z.string().nullable(),
  count: z.number(),
  name: z.string().optional(),
});
export type FacetTermResource = z.infer<typeof facetTermResourceSchema>;

export const FacetResource = z.object({
  name: ProvidersResponseFacetEnum,
  terms: z.array(facetTermResourceSchema),
  total: z.number(),
});
export type FacetResource = z.infer<typeof FacetResource>;

export const providerSearchResponseSchema = pagedResponseSchema.extend({
  facets: z.array(FacetResource),
  providers: z.array(providerResourceSchema),
});
export type ProviderSearchResponse = z.infer<
  typeof providerSearchResponseSchema
>;

export const providerDetailsResponseSchema = z.object({
  provider: providerDetailsResourceSchema,
});

export type ProviderDetailsResponse = z.infer<
  typeof providerDetailsResponseSchema
>;

// LIVE SLOTS
// Request
export const providerAvailabilityRequestSchema = z.object({
  providerId: z.string(), // "ehrExternalId" field from the provider
  departmentIds: z.array(z.string()), // "ehrExternalId" field from the location
  reasonForVisit: z.string(),
  startDate: z.string(), // YYYY-MM-DD
  endDate: z.string(),
});

export type ProviderAvailabilityRequest = z.infer<
  typeof providerAvailabilityRequestSchema
>;
// Response
export const providerSlotReasonForVisitSchema = z.object({
  id: z.string().optional(),
  name: z.string().optional(),
  visitTypeId: z.string(),
  departmentId: z.string(),
  hospitalAssociation: z.string(),
});

export const slotSchema = z.object({
  startTime: z.string(),
  timeZone: z.string(),
  reasonsForVisit: z.array(providerSlotReasonForVisitSchema),
});

export const providerDateSlotsSchema = z.object({
  date: z.string(),
  slots: z.array(slotSchema),
});

export const locationSlotsSchema = z.object({
  departmentId: z.string(),
  days: z.array(providerDateSlotsSchema),
});

export const providerAvailabilityResponseSchema = z.object({
  provider: z.object({
    id: z.string(),
    name: z.string(),
  }),
  departments: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      timezone: z.string(),
    }),
  ),
  schedule: z.array(locationSlotsSchema),
});

export type ProviderAvailabilityResponse = z.infer<
  typeof providerAvailabilityResponseSchema
>;

export const DEFAULT_SEARCH_PARAMS: ProvidersSearchCriteria = {
  page: 1,
  limit: 10,
  capabilities: [],
  sort: ProviderSortingEnum.enum.availability,
};
